/**
 * A workspace: which panels are on screen, and how the space is divided.
 *
 * ## Why columns rather than a free grid
 *
 * The obvious model is a drag-anywhere grid, and it is the wrong one here. A
 * free grid has to answer "what happens when this panel is dragged half over
 * that one", which is a large amount of interaction code guarding a state
 * nobody wants; and it produces layouts that cannot survive a window being
 * resized, which on a laptop that docks to a monitor is every day.
 *
 * Columns, each holding a vertical stack, is what a trading screen actually
 * is. It resizes predictably, it serialises to a few numbers, and every
 * operation on it — add, remove, move, resize — is a pure function over a
 * small structure, which is why the whole of this file is testable.
 *
 * ## A workspace is data the trader owns
 *
 * Stored server-side rather than per browser, because a trader who arranges
 * their screen on a desktop and opens the web app on a laptop has arranged it
 * once, not twice. That also means a layout outlives the build that wrote it,
 * which is what `normaliseWorkspace` is for.
 */
import { isPanelKind, panelSpec, type PanelKind } from './panels.js';

/**
 * How several charts on one screen stay in step.
 *
 * Groups rather than one global switch, because the question a trader is
 * answering is "sync THESE two", not "sync everything". A colour is the label
 * every platform that does this uses, and it is the only model that expresses
 * two independent pairs on the same screen.
 *
 * `none` is a real value and the default: a chart that joined a group by
 * accident jumps to another instrument's crosshair, which reads as the chart
 * having lost its place.
 */
export const SYNC_GROUPS = ['none', 'red', 'blue', 'green', 'amber'] as const;
export type SyncGroup = (typeof SYNC_GROUPS)[number];

export function isSyncGroup(v: unknown): v is SyncGroup {
  return typeof v === 'string' && (SYNC_GROUPS as readonly string[]).includes(v);
}

/**
 * What one instance of a panel is looking at.
 *
 * Every field optional, and absent means "the application's current default"
 * rather than a value of its own. That distinction is what lets a panel added
 * from the picker inherit whatever the trader is already on, while a panel
 * they deliberately pointed at CL stays on CL when everything else moves.
 */
export interface PaneConfig {
  /** For a symbol-scoped panel. Absent = follow the application default. */
  readonly symbol?: string;
  /** Charts only. */
  readonly timeframe?: string;
  /** Charts only — candles, hollow, bars, line. */
  readonly style?: string;
  /** Charts only. `none` unless the trader put it in a group. */
  readonly syncGroup?: SyncGroup;
}

/** One panel inside a column. */
export interface Pane {
  /**
   * Which instance this is.
   *
   * `chart`, `chart:2`, `chart:3`. Always the kind, optionally followed by a
   * colon and a number — enforced by `normaliseWorkspace`, which is what makes
   * `paneKind` total and lets `detached` stay a list of plain strings.
   *
   * The FIRST instance of a kind keeps `id === kind`, and that is not
   * cosmetic: it is what makes a workspace stored before instances existed
   * load unchanged, detached list included, with no migration script.
   */
  readonly id: string;
  readonly kind: PanelKind;
  /**
   * Share of its column's height. Relative, not absolute.
   *
   * Weights rather than pixels or percentages: a workspace has to mean the
   * same thing on a 13" laptop and a 32" monitor, and the only part that
   * carries across is the RATIO. Percentages would work too, but they invite
   * a bug where a rounding error leaves a layout summing to 99 and a one-pixel
   * gap nobody can find.
   */
  readonly weight: number;
  /** What this instance is looking at. Absent for a panel never configured. */
  readonly config?: PaneConfig;
}

export interface Column {
  readonly panes: readonly Pane[];
  /** Share of the workspace's width, on the same terms as `Pane.weight`. */
  readonly weight: number;
}

export interface Workspace {
  readonly id: string;
  readonly name: string;
  readonly columns: readonly Column[];
  /**
   * Panels the trader has torn off into their own window.
   *
   * Held on the workspace rather than in the desktop shell so that reopening
   * AEGIS restores the arrangement rather than the main window alone — and so
   * that a detached panel is not ALSO drawn inline, which is the bug this
   * field exists to make impossible.
   */
  readonly detached: readonly string[];
}

/**
 * The kind a pane id belongs to.
 *
 * Total, because `normaliseWorkspace` guarantees the shape. Returns null for
 * an id naming a panel this build does not have — a layout outlives the
 * release that wrote it, and the caller drops it rather than rendering a hole.
 */
export function paneKind(id: string): PanelKind | null {
  const kind = id.split(':')[0] ?? '';
  return isPanelKind(kind) ? kind : null;
}

/** Most columns a workspace may have. Beyond this nothing is readable. */
export const MAX_COLUMNS = 4;
/** Most panes in one column, for the same reason. */
export const MAX_PANES_PER_COLUMN = 5;
/** Most workspaces a trader may keep. A switcher is not a file browser. */
export const MAX_WORKSPACES = 8;

/**
 * Make a stored workspace safe to render.
 *
 * Everything here is a state a real user reaches, not defensive noise:
 *
 * - **A panel this build no longer has.** Layouts outlive releases.
 * - **A pane with no id, or an id shared with another.** Every layout stored
 *   before instances existed is in the first state, and this is the function
 *   that migrates them — see `assignId`. Two panes claiming one id is the
 *   second, and it makes every operation keyed on that id ambiguous.
 * - **A second copy of a panel that may only appear once.** Four rule trackers
 *   is one account's limits drawn four times. Charts and the order-flow panels
 *   are exempt: several of those IS the screen.
 * - **Weights of zero, or all zeroes.** A column with no share is a column
 *   that is on screen and one pixel wide, which reads as a rendering fault.
 * - **Nothing left at all.** Every panel removed is a blank application.
 * - **A detached pane also placed inline.** The one that produces two live
 *   charts of the same symbol in two windows, fighting over the same drawings.
 * - **A config naming a sync group or a symbol that is not one.** Stored JSON
 *   is written by more than one client and outlives all of them.
 */
/**
 * The id a pane keeps, or null when it is a duplicate to drop.
 *
 * The id IS the identity, and that single rule settles two states that look
 * identical in stored JSON:
 *
 * - **Two panes with the same id** are one instance written twice — a move
 *   that half-failed, or two browsers saving at once. The second is dropped.
 * - **Two panes with different ids** (`chart`, `chart:2`) are two instances
 *   the trader asked for. Both are kept.
 *
 * Renaming the second into a free id instead would turn every duplicated write
 * into a spurious extra chart, quietly, on a layout the trader never edited.
 *
 * A pane with no id is a layout written before instances existed. It takes the
 * bare kind — which is exactly what the old code implied — so those layouts
 * load unchanged, `detached: ['book']` still matches, and no migration script
 * has to run over stored rows. Old data could not hold two of a kind either
 * (the previous normalise collapsed them), so a legacy duplicate is a
 * duplicate and drops for the same reason.
 *
 * This function never INVENTS an instance. New ones are minted by
 * `nextPaneId` at the moment a trader adds one.
 */
function assignId(pane: Pane, used: ReadonlySet<string>): string | null {
  const wanted = typeof pane.id === 'string' ? pane.id : '';
  const valid = /^[a-z]+(:\d+)?$/.test(wanted) && paneKind(wanted) === pane.kind;
  const id = valid ? wanted : pane.kind;
  return used.has(id) ? null : id;
}

/**
 * A free id for a new instance of a kind.
 *
 * Minted here rather than in `normaliseWorkspace` because adding a pane is the
 * only moment an instance is actually created, and a function that cleans
 * stored data should not be able to bring one into existence.
 */
export function nextPaneId(ws: Workspace, kind: PanelKind): string {
  const taken = new Set(panesIn(ws));
  if (!taken.has(kind)) return kind;
  for (let n = 2; n <= MAX_COLUMNS * MAX_PANES_PER_COLUMN + 1; n += 1) {
    const candidate = `${kind}:${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${kind}:${taken.size + 1}`;
}

/**
 * A stored config, or nothing.
 *
 * Returns undefined for a config with nothing left in it, so an untouched pane
 * serialises without an empty object — a layout is compared field by field in
 * more than one test, and `{}` versus absent is a difference that means
 * nothing and fails one.
 */
function cleanConfig(config: PaneConfig | undefined): PaneConfig | undefined {
  if (!config || typeof config !== 'object') return undefined;
  const out: { -readonly [K in keyof PaneConfig]: PaneConfig[K] } = {};

  // Upper-cased and trimmed, because a symbol is matched against the
  // instrument master by exact string everywhere else in this codebase.
  if (typeof config.symbol === 'string' && config.symbol.trim()) {
    out.symbol = config.symbol.trim().toUpperCase();
  }
  if (typeof config.timeframe === 'string' && config.timeframe.trim()) {
    out.timeframe = config.timeframe.trim();
  }
  if (typeof config.style === 'string' && config.style.trim()) {
    out.style = config.style.trim();
  }
  // A group this build does not have is dropped rather than kept: an unknown
  // colour would sync with nothing and look like a chart that stopped
  // following the others.
  if (isSyncGroup(config.syncGroup) && config.syncGroup !== 'none') {
    out.syncGroup = config.syncGroup;
  }

  return Object.keys(out).length > 0 ? out : undefined;
}

export function normaliseWorkspace(input: Workspace): Workspace {
  const usedIds = new Set<string>();
  const seenKinds = new Set<PanelKind>();
  // Filtered to ids this build can place. An unknown kind here would otherwise
  // hold a slot against an inline pane that could have rendered.
  const detached = [...new Set(input.detached.filter((d) => paneKind(d) !== null))];
  for (const d of detached) usedIds.add(d);

  const columns: Column[] = [];
  for (const column of input.columns.slice(0, MAX_COLUMNS)) {
    const panes: Pane[] = [];
    for (const pane of column.panes) {
      if (!isPanelKind(pane.kind)) continue;

      const spec = panelSpec(pane.kind)!;
      // Only ONE of a panel that cannot repeat, whichever came first.
      if (!spec.multiple && seenKinds.has(pane.kind)) continue;

      // Null means this id is already placed — a duplicate write, or an
      // instance that is detached. Either way the copy in hand is stale.
      // Detached wins over inline: the trader dragged it out, and honouring
      // the inline copy is what puts two live charts on one symbol.
      const id = assignId(pane, usedIds);
      if (id === null) continue;

      usedIds.add(id);
      seenKinds.add(pane.kind);
      panes.push({
        id,
        kind: pane.kind,
        weight: positive(pane.weight),
        ...(cleanConfig(pane.config) ? { config: cleanConfig(pane.config)! } : {}),
      });
      if (panes.length >= MAX_PANES_PER_COLUMN) break;
    }
    // An empty column is dropped rather than rendered: a zero-width strip
    // between two panels reads as a broken divider.
    if (panes.length > 0) columns.push({ panes, weight: positive(column.weight) });
  }

  /*
    Something always renders.

    A workspace with every panel removed — or every panel detached — would
    otherwise be a blank window with a switcher in the corner. The chart is the
    fallback because it is the one panel that means something on its own.
  */
  if (columns.length === 0) {
    const fallback: PanelKind = detached.includes('chart') ? 'rules' : 'chart';
    columns.push({ panes: [{ id: fallback, kind: fallback, weight: 1 }], weight: 1 });
  }

  return {
    id: input.id,
    name: input.name.trim().slice(0, 40) || 'Workspace',
    columns,
    detached,
  };
}

/** A weight that can be divided by. Non-finite and non-positive become 1. */
function positive(n: number): number {
  return Number.isFinite(n) && n > 0 ? Math.min(n, 100) : 1;
}

/**
 * CSS `grid-template-columns` for a workspace.
 *
 * `minmax(min, weightfr)` rather than bare `fr`: a fraction alone lets a
 * column shrink under its panel's readable width, and the first thing to go is
 * a depth ladder turning into clipped numbers. The minimum is the widest
 * minimum of the panels actually in that column, so a column holding only
 * Notes is allowed to be narrow.
 */
export function gridTemplate(ws: Workspace, dividerPx = 0): string {
  const tracks = ws.columns.map((c) => `minmax(${columnMinWidth(c)}px, ${c.weight}fr)`);
  // Dividers are grid tracks rather than elements floating over the seam.
  // A handle positioned on top of a column edge has to be kept in step with
  // that edge by hand, and it drifts the moment the grid reflows; a track
  // cannot drift, because it IS the seam.
  return dividerPx > 0 ? tracks.join(` ${dividerPx}px `) : tracks.join(' ');
}

/**
 * The row template for one column, on the same terms.
 *
 * `minmax(0, Nfr)` and not `Nfr`: a grid row defaults to a minimum of
 * `auto`, so a pane containing something tall — a journal table, a long
 * analysis — refuses to shrink below its content and pushes every other pane
 * in the column off the bottom of the screen. The zero minimum is what makes
 * the weights mean what they say.
 */
export function columnTemplate(column: Column, dividerPx = 0): string {
  const tracks = column.panes.map((p) => `minmax(0, ${p.weight}fr)`);
  return dividerPx > 0 ? tracks.join(` ${dividerPx}px `) : tracks.join(' ');
}

export function columnMinWidth(column: Column): number {
  return column.panes.reduce(
    (widest, p) => Math.max(widest, panelSpec(p.kind)?.minWidth ?? 240),
    0,
  );
}

/** Narrowest window this workspace can be drawn in without clipping. */
export function workspaceMinWidth(ws: Workspace): number {
  return ws.columns.reduce((sum, c) => sum + columnMinWidth(c), 0);
}

/** Every pane id currently placed, detached ones included. */
export function panesIn(ws: Workspace): readonly string[] {
  return [...ws.columns.flatMap((c) => c.panes.map((p) => p.id)), ...ws.detached];
}

/** Every panel KIND currently placed, with a kind repeated once per instance. */
export function panelsIn(ws: Workspace): readonly PanelKind[] {
  return panesIn(ws)
    .map(paneKind)
    .filter((k): k is PanelKind => k !== null);
}

/** True when this workspace holds at least one of that kind. */
export function hasPanel(ws: Workspace, kind: PanelKind): boolean {
  return panelsIn(ws).includes(kind);
}

/** True when this exact instance is somewhere. */
export function hasPane(ws: Workspace, id: string): boolean {
  return panesIn(ws).includes(id);
}

/** The pane with this id, wherever it sits. Null when it is detached or gone. */
/**
 * The chart pane the screen's data and the replay controls belong to.
 *
 * Two rules, in order.
 *
 * A chart that FOLLOWS the screen's symbol wins. It draws the series the
 * header, the analysis panel and the order-flow panels are already reading, so
 * nothing has to be fetched twice, and replay steps through the instrument the
 * rest of the screen is describing.
 *
 * Failing that, the FIRST chart in the layout. Without this second rule a
 * workspace whose charts are all pinned to their own instruments has no
 * primary chart at all — and since the replay controls attach to that pane,
 * replay could not be entered from such a layout, with nothing on screen to
 * say why. Pinning every chart is an ordinary thing to do.
 *
 * The consequence of the fallback is that replay then runs on THAT pane's
 * instrument rather than the screen's. That is the intended behaviour — a
 * trader who pinned their only chart to YM and pressed replay means YM — but it
 * has to be said out loud in the interface, because the rest of the screen is
 * still about the screen's symbol.
 *
 * Detached panes are not eligible. They are in another window, and controls
 * rendered into a chart nobody is looking at are controls that do not exist.
 */
export function primaryChartPane(ws: Workspace): Pane | null {
  const charts: Pane[] = [];
  for (const column of ws.columns) {
    for (const pane of column.panes) if (pane.kind === 'chart') charts.push(pane);
  }
  return charts.find((p) => !p.config?.symbol) ?? charts[0] ?? null;
}

export function findPane(ws: Workspace, id: string): Pane | null {
  for (const column of ws.columns) {
    for (const pane of column.panes) if (pane.id === id) return pane;
  }
  return null;
}

/**
 * Change one instance's settings, leaving every other alone.
 *
 * Merged rather than replaced, so a caller setting a symbol does not silently
 * clear a timeframe the trader chose. `normaliseWorkspace` then drops whatever
 * the merge left meaningless.
 */
export function configurePane(
  ws: Workspace, id: string, config: PaneConfig,
): Workspace {
  if (!hasPane(ws, id)) return ws;
  return normaliseWorkspace({
    ...ws,
    columns: ws.columns.map((c) => ({
      ...c,
      panes: c.panes.map((p) => (p.id === id
        ? { ...p, config: { ...p.config, ...config } }
        : p)),
    })),
  });
}

/**
 * Add a panel, or another instance of one.
 *
 * For a kind that cannot repeat this moves the existing one; for a chart or an
 * order-flow panel it adds a second, which is the whole point of instances.
 * `normaliseWorkspace` hands the new pane its id.
 *
 * Appends to the shortest column, which is what a person means by "put it
 * somewhere sensible" — the alternative, always appending to the first, piles
 * everything into one column and leaves the others empty.
 *
 * `config` seeds the instance. A second chart added while the trader is
 * looking at CL should open on CL, not on whatever the application default
 * happens to be — but that is the CALLER's judgement, so it is passed rather
 * than inferred here.
 */
export function addPanel(
  ws: Workspace, kind: PanelKind, config?: PaneConfig,
): Workspace {
  const spec = panelSpec(kind);
  if (!spec) return ws;

  // A repeatable kind keeps what is already there; a single one moves.
  const without = spec.multiple ? ws : removePanel(ws, kind);
  const pane: Pane = {
    id: nextPaneId(without, kind), kind, weight: 1, ...(config ? { config } : {}),
  };

  const shortest = without.columns.reduce(
    (best, c, i) => (c.panes.length < without.columns[best]!.panes.length ? i : best),
    0,
  );
  const target = without.columns[shortest];
  if (!target || target.panes.length >= MAX_PANES_PER_COLUMN) {
    // The column is full and adding a new one is better than refusing, up to
    // the point where columns stop being readable.
    if (without.columns.length < MAX_COLUMNS) {
      return normaliseWorkspace({
        ...without,
        columns: [...without.columns, { panes: [pane], weight: 1 }],
      });
    }
    return ws;
  }

  const columns = without.columns.map((c, i) => (i === shortest
    ? { ...c, panes: [...c.panes, pane] }
    : c));
  return normaliseWorkspace({ ...without, columns });
}

/** Remove EVERY instance of a kind, inline or detached. */
export function removePanel(ws: Workspace, kind: PanelKind): Workspace {
  return normaliseWorkspace({
    ...ws,
    columns: ws.columns.map((c) => ({
      ...c, panes: c.panes.filter((p) => p.kind !== kind),
    })),
    detached: ws.detached.filter((d) => paneKind(d) !== kind),
  });
}

/**
 * Remove one instance, leaving the others.
 *
 * The operation a close button on a pane performs. Closing the third chart
 * must not take the other two with it, which is what `removePanel` would do.
 */
export function removePane(ws: Workspace, id: string): Workspace {
  return normaliseWorkspace({
    ...ws,
    columns: ws.columns.map((c) => ({
      ...c, panes: c.panes.filter((p) => p.id !== id),
    })),
    detached: ws.detached.filter((d) => d !== id),
  });
}

/**
 * Tear one instance out into its own window.
 *
 * It leaves the grid at the same moment, which is why this is one operation
 * rather than "detach" plus "remove". Two live charts on one symbol, in two
 * windows, fighting over the same drawings, is the failure this shape
 * prevents by construction.
 *
 * The pane's config travels with the id: the window has to open on the symbol
 * the pane was showing, not on the application default, or tearing off a CL
 * ladder produces an ES one.
 */
export function detachPane(ws: Workspace, id: string): Workspace {
  if (!hasPane(ws, id)) return ws;
  return normaliseWorkspace({
    ...ws,
    columns: ws.columns.map((c) => ({
      ...c, panes: c.panes.filter((p) => p.id !== id),
    })),
    detached: [...ws.detached, id],
  });
}

/** Put a detached instance back in the grid. */
export function attachPane(ws: Workspace, id: string): Workspace {
  if (!ws.detached.includes(id)) return ws;
  const kind = paneKind(id);
  if (!kind) return ws;
  return addPanel({ ...ws, detached: ws.detached.filter((d) => d !== id) }, kind);
}

/**
 * Move one instance to a named place, which is what a drag means.
 *
 * `addPanel` answers "put this somewhere sensible" and is right for a picker.
 * It is wrong for a drag: a trader who dropped the tape at the top of the
 * right-hand column meant the top of the right-hand column, and landing it at
 * the bottom of whichever column happened to be shortest reads as the drag
 * having failed.
 *
 * Out-of-range indices are clamped rather than refused. A drop lands wherever
 * a pointer was released, and the last pixel of a column is `panes.length`
 * rather than `panes.length - 1` — refusing that would make the bottom edge of
 * every column a dead zone.
 *
 * Moving a panel within its own column accounts for its own removal: dragging
 * the first pane to index 3 of a four-pane column puts it last, not
 * second-to-last, which is what the pointer was over.
 */
export function movePane(
  ws: Workspace, id: string, toColumn: number, toIndex: number,
): Workspace {
  const kind = paneKind(id);
  if (!kind || !hasPane(ws, id)) return ws;
  if (toColumn < 0 || toColumn >= ws.columns.length) return ws;

  const from = ws.columns.findIndex((c) => c.panes.some((p) => p.id === id));
  // Absent from every column means it is detached, and a drag into the grid is
  // it coming home. It arrives with no config because there is no pane to take
  // one from — the detached list holds ids, not settings.
  const pane: Pane = from === -1
    ? { id, kind, weight: 1 }
    : ws.columns[from]!.panes.find((p) => p.id === id)!;

  const target = ws.columns[toColumn]!;
  // A full column refuses rather than silently dropping the pane somewhere
  // else. Somewhere else is not where the trader let go.
  if (from !== toColumn && target.panes.length >= MAX_PANES_PER_COLUMN) return ws;

  const columns = ws.columns.map((c, i) => (i === toColumn || i === from
    ? { ...c, panes: c.panes.filter((p) => p.id !== id) }
    : c));

  const room = columns[toColumn]!.panes;
  const at = Math.min(Math.max(Math.floor(toIndex), 0), room.length);

  return normaliseWorkspace({
    ...ws,
    // Detached is cleared: a panel being dragged into the grid is a panel
    // coming back from its window, and leaving it in both is the state
    // `detachPanel` exists to make impossible.
    detached: ws.detached.filter((d) => d !== id),
    columns: columns.map((c, i) => (i === toColumn
      ? { ...c, panes: [...room.slice(0, at), pane, ...room.slice(at)] }
      : c)),
  });
}

/**
 * Resize a pane, taking the difference from the pane below it.
 *
 * The vertical twin of `resizeColumn`, and for the same reason: a horizontal
 * divider drag changes the two panes it sits between and nothing else. The
 * last pane in a column borrows from the one above instead, because there is
 * nothing below it to take from.
 */
export function resizePane(
  ws: Workspace, columnIndex: number, paneIndex: number, weight: number,
): Workspace {
  const column = ws.columns[columnIndex];
  if (!column) return ws;
  if (paneIndex < 0 || paneIndex >= column.panes.length) return ws;

  const neighbour = paneIndex + 1 < column.panes.length ? paneIndex + 1 : paneIndex - 1;
  if (neighbour < 0) return ws;

  const pair = column.panes[paneIndex]!.weight + column.panes[neighbour]!.weight;
  // Same floor as a column: a pane at weight 0 is on screen and unreadable,
  // which is worse than a divider that refused to move.
  const next = Math.min(Math.max(weight, pair * 0.15), pair * 0.85);

  return normaliseWorkspace({
    ...ws,
    columns: ws.columns.map((c, i) => (i === columnIndex
      ? {
        ...c,
        panes: c.panes.map((pane, j) => {
          if (j === paneIndex) return { ...pane, weight: next };
          if (j === neighbour) return { ...pane, weight: pair - next };
          return pane;
        }),
      }
      : c)),
  });
}

/**
 * Resize a column, taking the difference from its neighbour.
 *
 * From the NEIGHBOUR rather than from every column, because that is what a
 * divider drag means: the two panels either side of the handle change and
 * nothing else moves. Spreading it across all columns makes a drag on one
 * edge twitch the far side of the screen.
 */
export function resizeColumn(ws: Workspace, index: number, weight: number): Workspace {
  if (index < 0 || index >= ws.columns.length) return ws;
  const neighbour = index + 1 < ws.columns.length ? index + 1 : index - 1;
  if (neighbour < 0) return ws;

  const pair = ws.columns[index]!.weight + ws.columns[neighbour]!.weight;
  // Neither side may be squeezed to nothing: a column at weight 0 is on screen
  // and unreadable, which is worse than one that refused to move.
  const next = Math.min(Math.max(weight, pair * 0.15), pair * 0.85);

  return normaliseWorkspace({
    ...ws,
    columns: ws.columns.map((c, i) => {
      if (i === index) return { ...c, weight: next };
      if (i === neighbour) return { ...c, weight: pair - next };
      return c;
    }),
  });
}
